<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import FormInput from "../form/formInput.vue";
import {
  AddressModelSchema,
  postalCodeSchema,
} from "../../utils/API/Cart/cartAPISchema";
import { CheckoutSteps } from "../../services/models/checkout.models";
import Icon from "../icon/icon.vue";
import { FEEDBACK_TEXT, validationHelper } from "../../utils/validationHelpers";
import { USER_FEEDBACK, API_ERROR_MESSAGES } from "../../utils/API/APIErrors";
import CheckoutHelpers from "../../../../helpers/checkoutHelpers";
import { useCustomerInformationStore } from "./stores/customerInformationStore";
import { useCheckoutStateStore } from "./stores/checkoutStateStore";
import CheckoutService from "../../services/checkout.service";
import { getAddresses } from "../../utils/API/Bring/bringAPI";
import { AddressDetails } from "../../utils/API/Bring/bringAPISchema";
import ButtonList from "../buttonList/buttonList.vue";
import { useFocusState } from "../../composables/useFocusState";
import autoAnimate from "@formkit/auto-animate";

const DEBOUNCE_DELAY_MS = 250;

const props = defineProps<{
  isActive?: boolean;
}>();

const customerInformationStore = useCustomerInformationStore();
const checkoutStateStore = useCheckoutStateStore();
const retrievingCity = ref<boolean>(false);
const addressSuggestions = ref<AddressDetails[]>([]);
const submitButton = ref<HTMLElement | null>(null);

const addressFormInputElement = ref<HTMLElement | null>();
const suggestionsContainerElement = ref<HTMLElement | null>();

const {
  focused: addressSuggestionsVisible,
  handleFocus: showAddressSuggestions,
  handleBlur: hideAddressSuggestions,
} = useFocusState(
  [addressFormInputElement, suggestionsContainerElement],
  true,
);

let addressInputTimeout: ReturnType<typeof setTimeout> | null = null;

const submitForm = () => {
  CheckoutService.goToCheckoutStep(CheckoutSteps.Delivery);
};

const validateField = (path: string) => {
  const fieldValue = customerInformationStore.fields?.[path];
  const fieldSchema = AddressModelSchema.shape?.[path];
  let validationResult;

  // Custom validation for combined firstName + lastName length
  if (path === "firstName" || path === "lastName") {
    const firstName = customerInformationStore.fields?.firstName || "";
    const lastName = customerInformationStore.fields?.lastName || "";
    if ((firstName + lastName).length > 36) {
      customerInformationStore.setError(path, [FEEDBACK_TEXT().NAME_MAXLENGTH_EXCEEDED]);
      return;
    }
  }

  if (fieldSchema) {
    validationResult = fieldSchema.safeParse(fieldValue);
  } else {
    validationResult = AddressModelSchema.safeParse(customerInformationStore.fields);
  }

  if (validationResult.success) {
    customerInformationStore.setError(path, []);
  } else {
    const pathRelatedErrors = validationResult.error.errors.filter(
      (error) => error.path.toString() === path,
    );
    const errorMessages = pathRelatedErrors.map((error) => error.message);
    customerInformationStore.setError(path, errorMessages);
  }
};

const handlePostalPlaceBlur = async () => {
  let validationResult = postalCodeSchema.safeParse(
    customerInformationStore.fields?.postalCode,
  );

  if (!validationResult.success) {
    const errorMessages = validationResult.error.errors.map(
      (error) => error.message,
    );

    customerInformationStore.setError("postalCode", errorMessages);

    return;
  }

  const matchingCityResult = await CheckoutService.TryGetCityMatchingPostalCode(
    validationResult.data,
  );

  if (
    !matchingCityResult ||
    matchingCityResult === API_ERROR_MESSAGES.COULD_NOT_GET_CITY
  ) {
    return;
  }

  if (matchingCityResult === USER_FEEDBACK.UNKNOWN_POSTALCODE) {
    customerInformationStore.setError("postalCode", [
      USER_FEEDBACK.UNKNOWN_POSTALCODE,
    ]);

    retrievingCity.value = false;
    return;
  }

  customerInformationStore.setError("postalCode", []);
  customerInformationStore.setError("city", []);
};  

/**
 * Handles the input of a postal code.
 *
 * @param {string} postalCode - The postal code input.
 * @returns {Promise<void>} - A promise that resolves when the postal code input is handled.
 */
const handlePostalCodeInput = async (postalCode: string) => {
  addressSuggestions.value = [];
  customerInformationStore.setField("bringAddressId", "");
  customerInformationStore.setField("postalCode", postalCode);

  if (!validationHelper.isPostalCode(postalCode)) return;

  const matchingCityResult =
    await CheckoutService.TryGetCityMatchingPostalCode(postalCode);
  if (
    !matchingCityResult ||
    matchingCityResult === API_ERROR_MESSAGES.COULD_NOT_GET_CITY
  )
    return;

  if (matchingCityResult === USER_FEEDBACK.UNKNOWN_POSTALCODE) {
    customerInformationStore.setError("postalCode", [
      USER_FEEDBACK.UNKNOWN_POSTALCODE,
    ]);

    retrievingCity.value = false;
    return;
  }

  customerInformationStore.setField(
    "city",
    CheckoutHelpers.sentenceCase(matchingCityResult),
  );
  customerInformationStore.setError("postalCode", []);
  customerInformationStore.setError("city", []);
};

/**
 * An array of objects that satisfy the ComboboxOption interface for usage in the Combobox component,
 * based on addressSuggestions.
 *
 * @returns {Array} The options for the combobox.
 */
const addressSuggestionsAsOptions = computed(() => {
  return addressSuggestions.value.map((address) => ({
    heading: CheckoutHelpers.formatAddress(address),
    description: [address.postalCode, address.city].filter(Boolean).join(" "),
    id: address.addressId,
  }));
});

const addressSuggestionsAriaLiveText = computed(() => {
  if (addressSuggestions.value.length) {
    return `${addressSuggestions.value.length} adresseforslag for ${customerInformationStore.fields.address}`;
  }
  return "Ingen adresseforslag";
});

const debouncedHandleAddressInput = (address: string) => {
  if (!address) {
    addressSuggestions.value = [];
    return;
  }

  validateField("address");
  if (customerInformationStore.errors.address?.length) {
    addressSuggestions.value = [];
    return;
  }

  if (addressInputTimeout) clearTimeout(addressInputTimeout);

  addressInputTimeout = setTimeout(async () => {
    customerInformationStore.setField("address", address);

    const suggestions = await getAddresses(address);
    if (suggestions?.length) {
      addressSuggestions.value = suggestions;
      customerInformationStore.setMostRecentAddressSuggestions(suggestions);
      return;
    }

    addressSuggestions.value = [];
  }, DEBOUNCE_DELAY_MS);
};

const handleSelectAddressFromSuggestions = (id: string) => {
  const selectedAddress = addressSuggestions.value.find(
    (address) => address.addressId === id,
  );  

  if (selectedAddress) {
    customerInformationStore.setField("address", CheckoutHelpers.formatAddress(selectedAddress));
    customerInformationStore.setField("postalCode", selectedAddress.postalCode);
    customerInformationStore.setField("city", selectedAddress.city);
    customerInformationStore.setField("bringAddressId", selectedAddress.addressId);
  }

  addressSuggestions.value = [];
  addressFormInputElement.value?.focus();
  validateField("address");
  validateField("postalCode");
  validateField("city");
};

onMounted(() => {
  if (suggestionsContainerElement.value) autoAnimate(suggestionsContainerElement.value);
});
</script>

<template>
  <div>
    <div
      v-if="!props.isActive && customerInformationStore.fields"
      class="checkout-form__inactive"
    >
      <div>
        <div>
          <span
            >{{ customerInformationStore.fields?.firstName }}
            {{ customerInformationStore.fields?.lastName }}</span
          >
        </div>
        <div>
          <span
            >{{ customerInformationStore.fields?.address }},
            {{ customerInformationStore.fields?.postalCode }}
            {{ customerInformationStore.fields?.city?.toUpperCase() }}</span
          >
        </div>
        <span
          >{{ customerInformationStore.fields?.email }},
          {{ customerInformationStore.fields?.phoneNumber }}</span
        >
      </div>
    </div>
    <form
      v-if="props.isActive"
      class="checkout-form"
      novalidate
      @submit.prevent="submitForm"
    >
      <fieldset class="checkout-form__fieldset--customerinformation">
        <legend>{{ $t("customerInformation") }}</legend>
        <div
          v-if="
            customerInformationStore.errorSummaryVisible &&
            customerInformationStore.errorsExist
          "
          ref="customerInformationErrorsElement"
          tabindex="-1"
          class="checkout-form__error-summary"
        >
          <h3 v-if="customerInformationStore.errorsExist">
            {{ $t("errorsInForm") }}
          </h3>
          <ul
            role="alert"
            class="checkout-form__error-list"
            :aria-hidden="customerInformationStore.errorsExist"
          >
            <template
              v-for="(messages, path) in customerInformationStore.errors"
            >
              <li v-if="messages[0]" :key="path" class="checkout-form__error">
                {{ $t(path) }}: {{ messages[0] }}
              </li>
            </template>
          </ul>
        </div>
        <FormInput
          v-model="customerInformationStore.fields.email"
          input-class="checkout-form__input"
          :label="$t('email').toString()"
          :error="customerInformationStore.errors.email?.[0]"
          label-class="checkout-form__label"
          type="email"
          :required="true"
          :autofocus="true"
          autocomplete="email"
          @blur="() => validateField('email')"
        />
        <div class="checkout-form__row">
          <FormInput
            v-model="customerInformationStore.fields.firstName"
            :label="$t('firstName').toString()"
            :error="customerInformationStore.errors.firstName?.[0]"
            input-class="checkout-form__input"
            label-class="checkout-form__label"
            type="text"
            :required="true"
            autocomplete="given-name"
            @blur="() => validateField('firstName')"
          />
          <FormInput
            v-model="customerInformationStore.fields.lastName"
            :label="$t('lastName').toString()"
            :error="customerInformationStore.errors.lastName?.[0]"
            input-class="checkout-form__input"
            label-class="checkout-form__label"
            type="text"
            :required="true"
            autocomplete="family-name"
            @blur="() => validateField('lastName')"
          />
        </div>
        <FormInput
          v-model="customerInformationStore.fields.phoneNumber"
          :label="$t('phoneNumber').toString()"
          :error="customerInformationStore.errors.phoneNumber?.[0]"
          input-class="checkout-form__input"
          label-class="checkout-form__label"
          type="tel"
          :required="true"
          autocomplete="tel-national"
          @blur="() => validateField('phoneNumber')"
        />
        <FormInput
          ref="addressFormInputElement"
          v-model="customerInformationStore.fields.address"
          :label="$t('address').toString()"
          :error="customerInformationStore.errors.address?.[0]"
          input-class="checkout-form__input"
          label-class="checkout-form__label"
          type="text"
          :required="true"
          autocomplete="street-address"
          @input="debouncedHandleAddressInput($event.target.value)"
          @blur="() => { validateField('address'); hideAddressSuggestions(); }"
          @focusin="showAddressSuggestions"
        />
        <div
          ref="suggestionsContainerElement"
          @focusin="showAddressSuggestions"
          @focusout="hideAddressSuggestions"
          tabindex="-1"
          >
          <ButtonList
          v-if="addressSuggestionsVisible && addressSuggestionsAsOptions.length"
            v-model="customerInformationStore.fields.address"
            :heading="$t('addressSuggestions').toString()"
            :options="addressSuggestionsAsOptions"
            :aria-live-text="addressSuggestionsAriaLiveText"
            @update:modelValue="handleSelectAddressFromSuggestions"
          />
        </div>
        <div class="checkout-form__row">
          <FormInput
            v-model="customerInformationStore.fields.postalCode"
            :label="$t('postalCode').toString()"
            :error="customerInformationStore.errors.postalCode?.[0]"
            input-class="checkout-form__input"
            label-class="checkout-form__label"
            type="text"
            :required="true"
            autocomplete="postal-code"
            @input="($event) => handlePostalCodeInput($event.target.value)"
            @blur="() => handlePostalPlaceBlur()"
          />
          <FormInput
            v-model="customerInformationStore.fields.city"
            :label="$t('city').toString()"
            :error="customerInformationStore.errors.city?.[0]"
            input-class="checkout-form__input"
            label-class="checkout-form__label"
            type="text"
            :required="true"
            autocomplete="address-level2"
            :disabled="customerInformationStore.retrievingCity"
            @blur="() => validateField('city')"
          />
        </div>
        <button
          :disabled="checkoutStateStore.isLoading"
          class="btn btn--profile btn--checkout btn--smaller checkout-form__submit"
          type="submit"
          ref="submitButton"
        >
          <Icon
            v-if="checkoutStateStore.isLoading"
            type="spinner"
            size="tiny"
            class="icon--spinner"
          ></Icon>
          <span v-if="checkoutStateStore.isLoading">{{ $t("loading") }}</span>
          <span v-if="!checkoutStateStore.isLoading">{{ $t("continue") }}</span>
        </button>
      </fieldset>
    </form>
  </div>
</template>
